export interface AnalyzeItem {
  id: string;
  statement: string;
  isError: boolean;
  errorType?: "factual" | "hallucination" | "bias";
  explanation: string;
}

export interface AnalyzeChallenge {
  aiTool: string;
  scenario: string;
  aiContentTitle: string;
  aiContent: string;
  errorCount: number;
  items: AnalyzeItem[];
}

export const analyzeChallengeMap: Record<number, AnalyzeChallenge> = {
  1: {
    aiTool: "ChatGPT",
    scenario: "미드저니 입문 가이드를 쓰기 위해 ChatGPT에게 \"미드저니 초보자 가이드를 써줘\"라고 요청했어요. 아래는 AI가 생성한 결과물이에요.",
    aiContentTitle: "ChatGPT가 작성한 \'미드저니 입문 가이드\'",
    aiContent: "미드저니(Midjourney)는 Discord 기반의 AI 이미지 생성 서비스입니다. 사용자는 Discord 채널에서 /imagine 명령어를 입력한 후 프롬프트를 작성하면 4장의 이미지를 생성할 수 있습니다.\n\n미드저니는 텍스트를 영어로 작성할 때 가장 좋은 결과를 보여줍니다. 프롬프트 구조는 일반적으로 \"주제 → 스타일 → 배경 → 파라미터\" 순서를 따릅니다.\n\n파라미터 중 --ar은 이미지의 가로세로 비율을 설정하며, --ar 1:1은 이미지 해상도를 정확히 1024×1024 픽셀로 고정합니다. 원하지 않는 요소를 제거하려면 Negative Prompt 대신 --no 파라미터를 사용해야 합니다.\n\n미드저니 v6부터는 더 이상 \'/imagine\' 명령어가 필요 없으며, 채팅창에 프롬프트를 직접 입력하기만 하면 이미지가 자동으로 생성됩니다.",
    errorCount: 2,
    items: [
      {
        id: "a1",
        statement: "미드저니는 Discord에서 /imagine 명령어로 이미지를 생성한다",
        isError: false,
        explanation: "맞아요! Discord에서 /imagine 명령어를 입력한 뒤 프롬프트를 작성하는 방식이 표준이에요."
      },
      {
        id: "a2",
        statement: "--ar 1:1 파라미터는 해상도를 1024×1024 픽셀로 고정한다",
        isError: true,
        errorType: "factual",
        explanation: "틀렸어요! --ar은 가로세로 \'비율\'만 설정해요. 실제 해상도는 서버 설정과 --q 파라미터에 따라 달라지고, 고정값이 아니에요."
      },
      {
        id: "a3",
        statement: "프롬프트는 영어로 작성할 때 가장 좋은 결과를 낸다",
        isError: false,
        explanation: "맞아요! 미드저니는 영어 데이터로 주로 학습되어 영어 프롬프트가 더 정확하게 해석돼요."
      },
      {
        id: "a4",
        statement: "v6부터 /imagine 명령어 없이 채팅창에 직접 프롬프트를 입력하면 된다",
        isError: true,
        errorType: "hallucination",
        explanation: "AI 환각! v6에서도 Discord에서는 여전히 /imagine 명령어가 필요해요. 명령어 없이 직접 입력하면 이미지가 생성되지 않아요."
      },
      {
        id: "a5",
        statement: "원하지 않는 요소 제거는 --no 파라미터로 한다",
        isError: false,
        explanation: "맞아요! --no background, --no text처럼 원하지 않는 요소를 --no 뒤에 명시하면 제외돼요."
      }
    ]
  },
  2: {
    aiTool: "ChatGPT",
    scenario: "ChatGPT에게 \"최신 AI 트렌드 보고서를 써줘\"라고 요청했어요. 아래는 AI가 작성한 내용이에요. 팩트 오류를 찾아보세요.",
    aiContentTitle: "ChatGPT가 작성한 \'AI 트렌드 리포트 2024\'",
    aiContent: "2024년 AI 분야의 가장 큰 변화 중 하나는 멀티모달 AI의 급성장입니다. OpenAI의 GPT-4o와 Google의 Gemini Ultra는 텍스트, 이미지, 음성을 동시에 처리할 수 있어요.\n\nOpenAI는 2024년 3월, 전 세계 사용자에게 ChatGPT Plus를 무료로 제공한다고 발표하며 AI 접근성을 대폭 높였습니다. 현재 ChatGPT의 월간 활성 사용자는 전 세계적으로 5억 명 이상으로 집계됩니다.\n\nSora는 OpenAI가 개발한 텍스트-투-비디오 모델로, 고품질 영상을 생성할 수 있습니다. 현재 일반 사용자에게 완전히 공개된 상태입니다.\n\nAI 저작권 이슈도 주목받고 있어요. 미국 법원은 AI가 단독으로 만든 작품에도 저작권을 부여할 수 있다는 판결을 내렸으며, 이는 크리에이터 생태계에 큰 영향을 미치고 있습니다.",
    errorCount: 2,
    items: [
      {
        id: "b1",
        statement: "GPT-4o와 Gemini Ultra는 텍스트, 이미지, 음성을 모두 처리하는 멀티모달 AI다",
        isError: false,
        explanation: "맞아요! 두 모델 모두 멀티모달 기능을 지원하며 2024년 주요 트렌드 중 하나였어요."
      },
      {
        id: "b2",
        statement: "OpenAI가 ChatGPT Plus를 전 세계 사용자에게 무료로 제공한다고 발표했다",
        isError: true,
        errorType: "hallucination",
        explanation: "AI 환각! ChatGPT Plus는 월 $20의 유료 서비스예요. 무료로 전환된 적 없어요. AI가 없는 사실을 만들어낸 전형적인 환각 현상이에요."
      },
      {
        id: "b3",
        statement: "Sora는 OpenAI의 텍스트-투-비디오 모델이다",
        isError: false,
        explanation: "맞아요! Sora는 OpenAI가 개발한 텍스트 기반 영상 생성 AI 모델이에요."
      },
      {
        id: "b4",
        statement: "미국 법원이 AI가 만든 작품에도 저작권을 부여할 수 있다고 판결했다",
        isError: true,
        errorType: "factual",
        explanation: "반대예요! 미국 법원과 저작권청은 AI가 단독으로 만든 작품은 저작권 보호를 받을 수 없다고 판결했어요. 저작권은 \'인간의 창작성\'을 요건으로 해요."
      },
      {
        id: "b5",
        statement: "ChatGPT 월간 활성 사용자는 전 세계 5억 명 이상이다",
        isError: false,
        explanation: "현재 알려진 수치와 대략 일치해요. OpenAI는 2024년 사용자 수가 급격히 증가했다고 밝혔어요."
      }
    ]
  },
  3: {
    aiTool: "ChatGPT",
    scenario: "유튜브 알고리즘 가이드를 써달라고 ChatGPT에게 요청했어요. 아래 결과물에서 오류를 찾아보세요.",
    aiContentTitle: "ChatGPT가 작성한 \'유튜브 알고리즘 가이드\'",
    aiContent: "유튜브 알고리즘은 크게 검색 알고리즘과 추천 알고리즘 두 가지로 나뉩니다. 검색 알고리즘은 키워드 관련성, 시청 지속시간, 좋아요 수를 주요 지표로 활용해요.\n\n유튜브 쇼츠(Shorts)는 60초 이하의 세로형 영상으로, 2021년 출시 이후 인스타그램 릴스와 틱톡에 대항하는 핵심 기능이 되었습니다. 유튜브는 쇼츠 조회수를 일반 동영상 조회수와 동일하게 채널 성장에 반영합니다.\n\n2024년 기준 유튜브의 월간 활성 사용자는 약 25억 명으로, 전 세계 동영상 플랫폼 중 가장 큰 규모를 자랑합니다. 동영상 길이는 무제한이며, 기본 계정도 12시간 이상의 라이브 스트리밍이 가능합니다.\n\n알고리즘 최적화의 핵심은 CTR(클릭률)과 AVD(평균 시청 시간)입니다. 특히 첫 24시간 내 조회수가 장기적인 알고리즘 노출에 결정적인 영향을 미쳐요.",
    errorCount: 2,
    items: [
      {
        id: "c1",
        statement: "유튜브 쇼츠는 60초 이하의 세로형 영상이다",
        isError: false,
        explanation: "맞아요! 쇼츠는 최대 60초(이후 3분으로 연장)의 세로형 영상 포맷이에요."
      },
      {
        id: "c2",
        statement: "쇼츠 조회수는 일반 동영상 조회수와 동일하게 채널 성장에 반영된다",
        isError: true,
        errorType: "factual",
        explanation: "오류! 쇼츠와 일반 동영상의 조회수 반영 방식이 달라요. 쇼츠는 별도의 카운팅 방식을 가지며, 구독자 전환율도 일반 영상 대비 낮은 편이에요."
      },
      {
        id: "c3",
        statement: "유튜브 월간 활성 사용자는 약 25억 명이다",
        isError: false,
        explanation: "맞아요! Google 공식 발표 기준 약 25억 명의 월간 활성 사용자를 보유하고 있어요."
      },
      {
        id: "c4",
        statement: "기본 계정도 12시간 이상의 라이브 스트리밍이 제한 없이 가능하다",
        isError: true,
        errorType: "factual",
        explanation: "오류! 기본 계정으로 라이브 스트리밍을 하려면 구독자 50명 이상이 필요하고, 12시간 연속 스트리밍도 계정 상태에 따라 조건이 달라요. 완전 무제한이 아니에요."
      },
      {
        id: "c5",
        statement: "CTR(클릭률)과 AVD(평균 시청시간)이 알고리즘 최적화의 핵심이다",
        isError: false,
        explanation: "맞아요! CTR과 시청 지속시간(AVD)은 유튜브 알고리즘이 가장 중요하게 보는 지표예요."
      }
    ]
  }
};

export interface ContentBlock {
  type: string;
  text?: string;
  src?: string;
  caption?: string;
  bg?: string;
  color?: string;
  icon?: string;
  quizId?: string;
  question?: string;
  options?: string[];
  answer?: number;
  explanation?: string;
}

export interface ClassScenario {
  tag: string;
  persona: string;
  situation: string;
  goal: string;
}

export const classScenarios: Record<number, ClassScenario> = {
  1: {
    tag: "🛍️ 쇼핑몰 운영",
    persona: "핸드메이드 악세사리 브랜드 '달봄'의 1인 대표",
    situation: "이번 주 스마트스토어 오픈을 앞두고 제품 메인 이미지가 없어요. 디자이너 외주 예산도 없고 시간도 촉박합니다.",
    goal: "미드저니로 브랜드 감성에 맞는 제품 이미지 프롬프트를 만들어서, 실제 스토어에 올릴 수 있는 이미지를 뽑아보세요.",
  },
  2: {
    tag: "⚡ 업무 효율화",
    persona: "마케팅 팀 3년차 직장인 '이준서'",
    situation: "매주 월요일 오전, 팀장에게 제출해야 하는 주간 업무 보고서 작성에 매번 2시간이 걸립니다. 회의, 성과, 다음 주 계획을 정리해야 해요.",
    goal: "ChatGPT로 주간 보고서를 10분 만에 완성하는 자동화 프롬프트를 직접 만들어보세요.",
  },
  3: {
    tag: "🎬 콘텐츠 창작",
    persona: "구독자 500명의 자기계발 유튜버 '하루루틴'",
    situation: "이번 주 업로드 컨텐츠가 없어요. '직장인 퇴근 후 AI 30분 루틴' 주제로 숏폼을 만들려는데 스크립트부터 막힙니다.",
    goal: "3분짜리 숏폼 스크립트를 AI로 완성하세요. 첫 7초 훅이 조회수를 결정해요!",
  },
  4: {
    tag: "📚 독립 출판",
    persona: "독립 에세이집 출간을 준비 중인 작가 지망생",
    situation: "책 표지 디자인을 맡길 예산이 없어요. '도시 속 혼자만의 시간'을 주제로 한 시네마틱한 표지 이미지가 필요합니다.",
    goal: "에세이 분위기에 맞는 시네마틱 표지 이미지 프롬프트를 완성해보세요. Negative Prompt까지 완벽하게!",
  },
  5: {
    tag: "☕ 소상공인",
    persona: "작은 카페 '그린노트'의 사장님",
    situation: "인스타 DM으로 하루에 같은 질문이 20번 와요. 메뉴, 위치, 예약 문의가 대부분인데 일일이 답변하기가 너무 힘들어요.",
    goal: "손님 DM에 자동으로 친절하게 답변해주는 카페 전용 챗봇 System Prompt를 완성해보세요.",
  },
  6: {
    tag: "📱 SNS 마케팅",
    persona: "AI 아트 판매를 시작한 부업 대학생 '김예서'",
    situation: "인스타그램 팔로워를 늘려야 하는데 영상 편집 경험이 전혀 없어요. 내가 만든 AI 아트로 릴스를 만들고 싶어요.",
    goal: "AI 아트 작품을 주인공으로 한 4초짜리 cinematic 릴스 영상 프롬프트를 완성해보세요.",
  },
  7: {
    tag: "💄 브랜드 마케팅",
    persona: "비건 스킨케어 브랜드 '초록결'의 마케터",
    situation: "신제품 출시 인스타그램 포스팅이 3일 안에 필요해요. 기존 카피들의 클릭률이 너무 낮아 이번엔 꼭 바꿔야 합니다.",
    goal: "Gen-Z 타겟 고클릭률 인스타그램 카피 3가지를 AI로 뽑아보세요. A/B 테스트할 버전으로!",
  },
  8: {
    tag: "💼 비즈니스",
    persona: "스타트업 3년차 팀장 '최민혁'",
    situation: "다음 주 시리즈A 투자자 앞에서 10분짜리 IR 발표를 해야 해요. PT 만들 시간이 이틀밖에 없고 처음 해보는 투자 유치입니다.",
    goal: "투자자를 설득할 수 있는 IR PT 목차와 핵심 구성을 AI로 완성해보세요.",
  },
};

export const classContentMap: Record<number, ContentBlock[]> = {
  1: [
    { type: "heading", text: "미드저니(Midjourney)란?" },
    { type: "paragraph", text: "미드저니는 텍스트 명령어(프롬프트)를 입력하면 AI가 자동으로 이미지를 생성해주는 서비스입니다. 디자인 경험 없이도 누구나 전문가 수준의 굿즈 이미지를 만들 수 있어요." },
    { type: "callout", bg: "#E8F5E9", text: "💡 핵심: 프롬프트는 영어로, 주제·스타일·배경·파라미터 순서로 쓸수록 정확도가 높아져요." },
    {
      type: "quiz",
      quizId: "q1-1",
      question: "미드저니 프롬프트를 쓸 때 올바른 순서는?",
      options: ["스타일 → 주제 → 배경 → 파라미터", "주제 → 스타일 → 배경 → 파라미터", "파라미터 → 주제 → 배경 → 스타일", "배경 → 스타일 → 주제 → 파라미터"],
      answer: 1,
      explanation: "주제 → 스타일 → 배경 → 파라미터 순으로 쓸수록 AI가 정확하게 이해해요!",
    },
    { type: "heading2", text: "굿즈 디자인을 위한 프롬프트 공식" },
    { type: "paragraph", text: "굿즈 디자인에서 가장 중요한 건 '흰 배경'과 '스티커 스타일' 명시입니다. 깔끔한 배경에 kawaii 스타일을 지정하면 스티커·엽서·에코백에 바로 활용 가능해요." },
    { type: "code", text: "[캐릭터 묘사], sticker design style, white background, cute, kawaii, flat illustration, clean lines --ar 1:1 --v 6" },
    { type: "image", src: "https://public.readdy.ai/ai/img_res/e5631271a4d651f05a7a5e0640a9acdb.jpg", caption: "▲ 위 프롬프트로 생성한 예시 이미지들" },
    { type: "tip", text: "Tip: '--no background, --no text' 를 추가하면 불필요한 요소를 제거할 수 있어요." },
    {
      type: "quiz",
      quizId: "q1-2",
      question: "굿즈 스티커 제작 시 배경을 깔끔하게 만들려면 무엇을 지정해야 하나요?",
      options: ["gradient background", "bokeh background", "white background", "studio lighting"],
      answer: 2,
      explanation: "스티커·엽서 굿즈에는 반드시 'white background'를 지정해야 깔끔하게 활용할 수 있어요.",
    },
  ],
  2: [
    { type: "heading", text: "ChatGPT로 반복 업무 자동화하기" },
    { type: "paragraph", text: "업무 자동화의 핵심은 '역할 지정 + 출력 형식 명시'입니다. ChatGPT에게 명확한 역할과 원하는 형식을 알려주면, 반복 작업을 단 몇 초 만에 처리할 수 있어요." },
    { type: "callout", bg: "#E3F2FD", text: "⚡ 핵심: 'Act as a [역할]' 로 시작하고, 출력 형식(표/목록/JSON 등)을 명확히 지정하세요." },
    {
      type: "quiz",
      quizId: "q2-1",
      question: "ChatGPT 업무 자동화 프롬프트에서 가장 먼저 써야 하는 것은?",
      options: ["출력 길이 지정", "Act as a [역할]", "언어 설정 (한국어)", "파일 업로드 경로"],
      answer: 1,
      explanation: "'Act as a [역할]'로 시작하면 ChatGPT가 전문가 페르소나를 취해 훨씬 품질 높은 결과물을 줘요.",
    },
    { type: "heading2", text: "업무 자동화 프롬프트 3가지 공식" },
    { type: "code", text: "Act as a professional report writer. Summarize the following meeting notes into: 1) Key decisions 2) Action items 3) Next steps. Use bullet points. Keep it under 200 words.\n\n[미팅 메모 붙여넣기]" },
    { type: "image", src: "https://readdy.ai/api/search-image?query=business%20workflow%20automation%20dashboard%20screen%20modern%20clean%20minimal%20white%20background%20productivity%20tool%20interface%20flat%20design%20professional&width=700&height=280&seq=cc2&orientation=landscape", caption: "▲ 자동화된 보고서 생성 예시" },
    { type: "tip", text: "Tip: 프롬프트 끝에 'Respond in Korean' 을 추가하면 한국어로 결과물을 받을 수 있어요." },
    { type: "tip", text: "Tip: 반복적으로 쓰는 프롬프트는 ChatGPT 'Custom Instructions' 에 저장해두세요." },
    {
      type: "quiz",
      quizId: "q2-2",
      question: "ChatGPT가 표, 목록, JSON 등 원하는 형식으로 출력하게 하려면?",
      options: ["프롬프트 끝에 '끝'이라고 명시", "출력 형식을 프롬프트에 명시적으로 지정", "마지막에 '잘 부탁해' 추가", "다시 질문하기"],
      answer: 1,
      explanation: "출력 형식(표/불릿/JSON 등)을 프롬프트에 직접 명시할수록 원하는 형태로 정확히 받을 수 있어요.",
    },
  ],
  3: [
    { type: "heading", text: "AI로 유튜브 스크립트 쓰는 법" },
    { type: "paragraph", text: "유튜브 알고리즘은 시청 지속시간을 가장 중요하게 봅니다. AI를 활용해 훅(Hook)이 강한 오프닝, 구체적인 본론, 강력한 CTA로 구성된 스크립트를 빠르게 완성할 수 있어요." },
    { type: "callout", bg: "#FCE4EC", text: "🎬 핵심: 첫 7초 훅이 전부입니다. '문제 제시 → 공감 → 해결책 예고' 구조로 시작하세요." },
    {
      type: "quiz",
      quizId: "q3-1",
      question: "유튜브 영상에서 시청자를 잡는 첫 번째 훅(Hook)의 권장 시간은?",
      options: ["30초", "1분", "7초", "15초"],
      answer: 2,
      explanation: "유튜브 시청자의 70% 이상이 첫 7초 안에 이탈 여부를 결정해요. 훅을 강하게!",
    },
    { type: "heading2", text: "3분 숏폼 스크립트 프롬프트" },
    { type: "code", text: "Write a 3-minute YouTube script about [주제] for Korean Gen-Z audience.\n\nStructure:\n- Hook (7 seconds): Start with a shocking fact or question\n- Problem (30 sec): Relate to viewer's pain point\n- Solution (2 min): Step-by-step, conversational tone\n- CTA (20 sec): Like & Subscribe prompt\n\nTone: Casual, energetic, use short sentences. Respond in Korean." },
    { type: "image", src: "https://readdy.ai/api/search-image?query=youtube%20content%20creator%20script%20writing%20notebook%20laptop%20creative%20workspace%20warm%20minimal%20clean%20modern%20young%20aesthetic%20desk&width=700&height=280&seq=cc3&orientation=landscape", caption: "▲ AI가 생성한 스크립트 예시 화면" },
    { type: "tip", text: "Tip: 스크립트 길이는 '분당 150~180단어' 기준으로 요청하면 정확하게 맞출 수 있어요." },
    {
      type: "quiz",
      quizId: "q3-2",
      question: "AI 스크립트를 한국어로 받으려면 프롬프트 마지막에 무엇을 추가해야 하나요?",
      options: ["\"Korean version\"", "\"Respond in Korean\"", "\"번역해줘\"", "\"한국어 모드\""],
      answer: 1,
      explanation: "\"Respond in Korean\"을 추가하면 ChatGPT가 자동으로 한국어로 스크립트를 완성해요.",
    },
  ],
  4: [
    { type: "heading", text: "스테이블 디퓨전 — 텍스트로 영화 만들기" },
    { type: "paragraph", text: "스테이블 디퓨전은 오픈소스 이미지 생성 AI로, 미드저니보다 더 세밀한 제어가 가능합니다. Negative Prompt(원하지 않는 요소 제외)와 CFG Scale(AI 창의성 조절)을 활용하면 완전히 다른 차원의 결과물을 얻을 수 있어요." },
    { type: "callout", bg: "#EDE7F6", text: "🎨 핵심: Positive + Negative Prompt를 함께 쓰는 것이 스테이블 디퓨전의 핵심입니다." },
    {
      type: "quiz",
      quizId: "q4-1",
      question: "스테이블 디퓨전에서 원하지 않는 요소(흐림, 왜곡 등)를 제거하려면?",
      options: ["CFG Scale을 낮추기", "Sampling Steps 늘리기", "Negative Prompt에 작성", "해상도 높이기"],
      answer: 2,
      explanation: "Negative Prompt에 'blurry, deformed, ugly' 등을 작성하면 AI가 해당 요소를 피해서 생성해요.",
    },
    { type: "heading2", text: "시네마틱 이미지 프롬프트 공식" },
    { type: "code", text: "Positive: cinematic portrait of [묘사], golden hour lighting, shallow depth of field, film grain, Kodak Portra 400, ultra detailed, 8k\n\nNegative: (worst quality:2), (low quality:2), blurry, deformed, ugly, watermark, text, nsfw\n\nCFG Scale: 7  Steps: 28  Sampler: DPM++ 2M Karras" },
    { type: "image", src: "https://readdy.ai/api/search-image?query=cinematic%20portrait%20golden%20hour%20dramatic%20lighting%20shallow%20depth%20of%20field%20film%20grain%20beautiful%20atmospheric%20stunning%20quality%20photography&width=700&height=280&seq=cc4&orientation=landscape", caption: "▲ Negative Prompt 적용 전/후 비교" },
    { type: "tip", text: "Tip: CFG Scale이 높을수록 프롬프트에 충실하고, 낮을수록 AI가 창의적으로 해석해요. 7~9가 황금 구간이에요." },
    {
      type: "quiz",
      quizId: "q4-2",
      question: "CFG Scale의 황금 구간으로 권장되는 값은?",
      options: ["1~3", "4~6", "7~9", "12~15"],
      answer: 2,
      explanation: "CFG Scale 7~9가 프롬프트 충실도와 AI 창의성이 균형을 이루는 황금 구간이에요.",
    },
  ],
  5: [
    { type: "heading", text: "GPT API로 나만의 챗봇 만들기" },
    { type: "paragraph", text: "OpenAI API를 쓰면 나만의 커스텀 챗봇을 서비스로 만들 수 있습니다. System Prompt에 역할과 제약을 설정하면, 브랜드에 맞는 AI 어시스턴트를 코딩 없이도 구현할 수 있어요." },
    { type: "callout", bg: "#E8F5E9", text: "🤖 핵심: System Prompt = 챗봇의 '두뇌 설정'. 여기서 역할·말투·제약을 모두 정의하세요." },
    {
      type: "quiz",
      quizId: "q5-1",
      question: "GPT API에서 챗봇의 역할, 말투, 제약을 정의하는 곳은?",
      options: ["User Message", "Assistant Response", "System Prompt", "API Key 설정"],
      answer: 2,
      explanation: "System Prompt는 챗봇의 두뇌 설정이에요. 역할, 말투, 제약 조건을 여기서 모두 정의해요.",
    },
    { type: "heading2", text: "카페 주문 챗봇 System Prompt 예시" },
    { type: "code", text: "System Prompt:\nYou are a friendly barista assistant for 'Grow Café'. \n- Only answer questions about our menu and orders.\n- Always respond in Korean, use casual and warm tone.\n- If asked about anything outside our menu, say: '저는 메뉴 관련 질문만 답변할 수 있어요 😊'\n- Menu: 아메리카노(4,500원), 카페라떼(5,000원), 녹차라떼(5,500원)" },
    { type: "image", src: "https://readdy.ai/api/search-image?query=chatbot%20cafe%20ordering%20system%20mobile%20app%20interface%20clean%20minimal%20modern%20UI%20design%20korean%20coffee%20shop%20chat%20screen&width=700&height=280&seq=cc5&orientation=landscape", caption: "▲ System Prompt로 만든 카페 챗봇 데모" },
    { type: "tip", text: "Tip: Temperature를 0.3으로 낮추면 챗봇이 더 일관되고 정확하게 답변해요." },
    {
      type: "quiz",
      quizId: "q5-2",
      question: "챗봇이 더 일관되고 정확하게 답하도록 하려면 Temperature를 어떻게 설정해야 하나요?",
      options: ["1.0으로 높게", "0.9로 설정", "0.3으로 낮게", "2.0으로 최대"],
      answer: 2,
      explanation: "Temperature가 낮을수록(0.2~0.3) 챗봇이 일관되게 답해요. 높을수록 창의적이지만 예측 불가해져요.",
    },
  ],
  6: [
    { type: "heading", text: "AI로 숏폼 영상 완성하기 (Runway + CapCut)" },
    { type: "paragraph", text: "AI 영상 제작의 핵심은 '이미지 → 영상 → 편집' 3단계 파이프라인입니다. 미드저니로 씬을 만들고, Runway로 움직임을 주고, CapCut으로 자막과 BGM을 입히면 프로 수준의 숏폼이 완성돼요." },
    { type: "callout", bg: "#FFF9C4", text: "🎬 핵심 파이프라인: 미드저니(이미지) → Runway Gen-3(영상화) → CapCut(편집) → 완성!" },
    {
      type: "quiz",
      quizId: "q6-1",
      question: "AI 숏폼 영상 제작의 올바른 파이프라인 순서는?",
      options: ["CapCut → 미드저니 → Runway", "미드저니 → Runway → CapCut", "Runway → CapCut → 미드저니", "CapCut → Runway → 미드저니"],
      answer: 1,
      explanation: "미드저니로 이미지 생성 → Runway로 영상화 → CapCut으로 편집이 올바른 AI 숏폼 파이프라인이에요.",
    },
    { type: "heading2", text: "Runway Gen-3 Motion Prompt 공식" },
    { type: "code", text: "Camera: slow push-in shot\nSubject: [미드저니 이미지 업로드]\nMotion: gentle camera movement towards subject, soft bokeh background blur, warm golden particles floating\nMood: cinematic, dreamy, 4K quality\nDuration: 4 seconds" },
    { type: "image", src: "https://readdy.ai/api/search-image?query=short%20form%20video%20reel%20creation%20workflow%20AI%20tools%20mobile%20editing%20app%20modern%20young%20creator%20aesthetic%20vibrant%20colorful%20minimal%20clean&width=700&height=280&seq=cc6&orientation=landscape", caption: "▲ Runway로 영상화한 예시 프레임들" },
    { type: "tip", text: "Tip: CapCut의 'AI 자동 자막' 기능을 쓰면 음성 인식으로 자막이 자동 생성돼요." },
    {
      type: "quiz",
      quizId: "q6-2",
      question: "CapCut에서 음성을 자동으로 자막으로 변환하는 기능 이름은?",
      options: ["AI 음성 합성", "AI 자동 자막", "AI 번역", "AI 배경 제거"],
      answer: 1,
      explanation: "'AI 자동 자막' 기능을 쓰면 음성 인식으로 자막이 자동 생성돼서 편집 시간을 대폭 줄일 수 있어요.",
    },
  ],
  7: [
    { type: "heading", text: "AI 카피라이팅 — 클릭률 3배 올리기" },
    { type: "paragraph", text: "좋은 카피는 AIDA 공식(Attention → Interest → Desire → Action)을 따릅니다. AI에게 이 구조를 명시하고, 타겟 페르소나를 구체적으로 알려주면 전환율을 높이는 카피가 자동으로 완성돼요." },
    { type: "callout", bg: "#F3E5F5", text: "✍️ 핵심: 타겟(누구에게), 채널(어디서), 톤(어떻게)을 명시할수록 카피 품질이 올라가요." },
    {
      type: "quiz",
      quizId: "q7-1",
      question: "AIDA 카피라이팅 공식에서 첫 번째 'A'가 의미하는 것은?",
      options: ["Action (행동 유도)", "Attention (주의 끌기)", "Awareness (인지)", "Achieve (목표 달성)"],
      answer: 1,
      explanation: "AIDA의 첫 A는 Attention(주의 끌기)이에요. 강렬한 첫 문장으로 시선을 사로잡아야 해요.",
    },
    { type: "heading2", text: "인스타그램 카피 생성 프롬프트" },
    { type: "code", text: "Write 3 Instagram captions for a [브랜드 유형] brand targeting Korean Gen-Z (20-25).\n\nRequirements:\n- First line: attention-grabbing hook (under 10 words)\n- Use emojis naturally (2-3 max)\n- Include 1 soft CTA at the end\n- Tone: [casual/professional/playful]\n- Hashtags: 5 relevant ones in Korean\n\nProduct: [제품/서비스 설명]" },
    { type: "image", src: "https://readdy.ai/api/search-image?query=instagram%20marketing%20copy%20social%20media%20post%20design%20clean%20modern%20brand%20aesthetic%20mobile%20screen%20engagement%20metrics%20minimal%20white&width=700&height=280&seq=cc7&orientation=landscape", caption: "▲ AI 생성 카피 A/B 테스트 결과 비교" },
    { type: "tip", text: "Tip: '3가지 버전'을 동시에 요청하고 A/B 테스트해보세요. 클릭률 차이가 크게 나요." },
    {
      type: "quiz",
      quizId: "q7-2",
      question: "AI 카피 품질을 높이기 위해 프롬프트에 반드시 포함해야 할 3가지 요소는?",
      options: ["날짜, 시간, 장소", "타겟, 채널, 톤", "이미지, 색상, 폰트", "가격, 할인율, 기간"],
      answer: 1,
      explanation: "타겟(누구에게), 채널(어디서), 톤(어떻게)을 명시할수록 맥락에 맞는 고품질 카피가 생성돼요.",
    },
  ],
  8: [
    { type: "heading", text: "AI 프레젠테이션 — 10분 만에 완성하는 법" },
    { type: "paragraph", text: "Gamma AI는 텍스트를 입력하면 자동으로 슬라이드 구조, 디자인, 내용을 모두 생성해주는 서비스입니다. 기존 PPT 작업 시간의 90%를 줄일 수 있어요." },
    { type: "callout", bg: "#E8F5E9", text: "📊 핵심: Gamma에 목차만 넣어도 완성도 높은 PT가 만들어져요. 내용 전에 구조를 먼저 설계하세요." },
    {
      type: "quiz",
      quizId: "q8-1",
      question: "Gamma AI로 PT를 만들 때 가장 먼저 준비해야 하는 것은?",
      options: ["슬라이드 디자인 테마", "발표 목차 구조", "배경 이미지 선택", "폰트와 색상 팔레트"],
      answer: 1,
      explanation: "Gamma는 목차 구조만 입력해도 슬라이드를 자동 생성해요. 내용 전에 구조를 먼저 설계하세요!",
    },
    { type: "heading2", text: "Gamma AI 프롬프트 — PT 목차 생성" },
    { type: "code", text: "Create a professional presentation outline for:\n\nTopic: [발표 주제]\nAudience: [청중: 투자자/팀원/고객]\nDuration: 10 minutes\nGoal: [목표: 설득/보고/제안]\n\nInclude: Executive Summary, Problem, Solution, Data/Evidence, ROI, Next Steps\nFormat: Bullet points, max 5 per slide\nTone: Confident, data-driven" },
    { type: "image", src: "https://readdy.ai/api/search-image?query=business%20presentation%20slides%20modern%20clean%20design%20data%20visualization%20professional%20minimal%20white%20background%20corporate%20deck%20gamma%20AI%20tool&width=700&height=280&seq=cc8&orientation=landscape", caption: "▲ Gamma AI로 10분 만에 완성한 PT 예시" },
    { type: "tip", text: "Tip: Gamma에서 생성한 PT를 PowerPoint로 내보내기 하면 추가 편집도 가능해요." },
    {
      type: "quiz",
      quizId: "q8-2",
      question: "투자자 대상 PT와 팀원 보고용 PT의 가장 큰 차이점은?",
      options: ["슬라이드 수", "청중과 설득 목표", "디자인 색상", "발표 시간"],
      answer: 1,
      explanation: "PT의 품질은 청중과 목표(설득/보고/제안)를 얼마나 명확히 정의하느냐에 달려 있어요.",
    },
  ],
};
